import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import Mascot from "@/components/Mascot";
import RewardMountain from "@/components/RewardMountain";
import { api } from "@/lib/api";
import { startAmbient, stopAmbient, playStarDing } from "@/lib/ambient";
import { playCongratsBurst, playEndChime, speak, stopSpeaking, warmUpVoices } from "@/lib/audio";

const CATEGORY_LABEL = {
  energy:     { label: "Énergie",   emoji: "⚡️" },
  creative:   { label: "Création",  emoji: "🎨" },
  wisdom:     { label: "Sagesse",   emoji: "📖" },
  daily_life: { label: "Quotidien", emoji: "🧺" },
  calm:       { label: "Calme",     emoji: "🌙" },
};

const RATINGS = [
  { id: "loved",    emoji: "❤️", label: "Adoré" },
  { id: "neutral",  emoji: "😐", label: "Neutre" },
  { id: "disliked", emoji: "👎", label: "Pas aimé" },
];

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function Journey() {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState(null);
  // Latest "completion meta" returned by the backend (awarded stars, child totals).
  const [progress, setProgress] = useState({
    awarded_stars: 0,
    child_total_stars: 0,
    child_star_goal: 50,
  });
  // Non-null while we're showing the transition screen between two missions.
  const [transition, setTransition] = useState(null);

  useEffect(() => {
    warmUpVoices();
    api.getJourney(journeyId)
      .then((j) => {
        setJourney(j);
        // Hydrate the mountain from the child's persisted totals.
        if (j.child) {
          setProgress((p) => ({
            ...p,
            child_total_stars: j.child.total_stars ?? 0,
            child_star_goal:   j.child.star_goal   ?? 50,
          }));
        }
        try {
          localStorage.setItem("mlou_journey_id", j.id);
        } catch (_) {}
      })
      .catch((e) => {
        console.error(e);
        setError("Mission introuvable. Recommencez la matinée.");
      });
    return () => {
      stopSpeaking();
      stopAmbient();
    };
  }, [journeyId]);

  const currentMission = useMemo(
    () => journey?.missions?.find((m) => m.status === "active"),
    [journey],
  );
  const completedCount = journey?.missions?.filter((m) => m.status === "done").length ?? 0;
  const totalCount = journey?.missions?.length ?? 0;
  const allDone = totalCount > 0 && completedCount === totalCount;

  // When the journey finishes, hop to the wrap-up questionnaire.
  useEffect(() => {
    if (!journey || !allDone) return;
    const t = setTimeout(() => navigate(`/journey/${journey.id}/wrap-up`), 1500);
    return () => clearTimeout(t);
  }, [journey, allDone, navigate]);

  if (error) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
        <p className="mb-4 text-[#5b4b6b]">{error}</p>
        <Link to="/" className="text-[#ff7043] underline">Retour à l'accueil</Link>
      </main>
    );
  }

  if (!journey) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md items-center justify-center px-6">
        <p className="text-[#5b4b6b]">Préparation de la matinée magique…</p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-8">
      <header className="mb-6 flex items-center justify-between">
        <Link to="/" className="text-sm text-[#5b4b6b]">← Accueil</Link>
        <span className="text-sm font-medium text-[#3b2a4a]">
          {completedCount} / {totalCount} missions
        </span>
      </header>

      <ProgressBar value={completedCount} max={totalCount} />

      {allDone ? (
        <JourneyComplete journey={journey} />
      ) : transition ? (
        <TransitionCard
          data={transition}
          onContinue={() => {
            const payload = transition.payload;
            setTransition(null);
            setJourney(payload.journey);
          }}
        />
      ) : (
        currentMission && (
          <ActiveMission
            mission={currentMission}
            journeyId={journey.id}
            onComplete={(payload) => {
              // Update star totals + handle reward toast immediately.
              setProgress({
                awarded_stars:     payload.awarded_stars     ?? 0,
                child_total_stars: payload.child_total_stars ?? 0,
                child_star_goal:   payload.child_star_goal   ?? 50,
              });
              if (payload.reward_just_reached) {
                toast.success("🏆 Récompense débloquée ! Bravo !", { duration: 6000 });
                if (payload.certificate?.url) {
                  setTimeout(() => {
                    toast.message("📜 Certificat prêt — cliquez pour ouvrir", {
                      duration: 10000,
                      action: {
                        label: "Voir",
                        onClick: () => window.open(payload.certificate.url, "_blank"),
                      },
                    });
                  }, 1500);
                }
              }

              const nextMission = payload.journey?.missions?.find((m) => m.status === "active");
              const finishedMission = currentMission;

              // If there's another mission queued, show the transition
              // screen instead of jumping straight in.
              if (nextMission) {
                setTransition({
                  finishedMission,
                  nextMission,
                  congratsUrl:    finishedMission.congrats_voice_url,
                  congratsText:   finishedMission.congrats_text,
                  congratsChar:   finishedMission.congrats_character,
                  congratsPitch:  finishedMission.voice_pitch_boost,
                  transitionUrl:  finishedMission.transition_voice_url,
                  transitionText: finishedMission.transition_text,
                  transitionChar: finishedMission.transition_character,
                  transitionPitch: nextMission.voice_pitch_boost,
                  payload,
                });
              } else {
                // Last mission — apply the new journey now (wrap-up flow).
                setJourney(payload.journey);
              }
            }}
          />
        )
      )}

      <UpcomingList missions={journey.missions} />

      <div className="mt-6">
        <RewardMountain
          total={progress.child_total_stars}
          goal={progress.child_star_goal}
        />
      </div>
    </main>
  );
}

function ProgressBar({ value, max }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-white/70">
      <div
        className="h-full rounded-full bg-[#ff8a65] transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ActiveMission({ mission, journeyId, onComplete }) {
  const totalSeconds = mission.duration_minutes * 60;
  const minSeconds   = mission.minimum_duration_seconds ?? 0;
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [running, setRunning] = useState(true);  // auto-start per PRD §7.3
  const [submittingRating, setSubmittingRating] = useState(false);
  const [audioOn, setAudioOn] = useState(true);
  const [musicOn, setMusicOn] = useState(true);
  const [lastStars, setLastStars] = useState(null); // visual feedback after completion
  const endedRef = useRef(false);
  const startedAtRef = useRef(Date.now());

  const minLockRemaining = Math.max(0, minSeconds - secondsElapsed);
  const canFinish = minLockRemaining === 0;

  // When the active mission changes:
  // 1. Reset the timer
  // 2. Speak the character message ("Neo a préparé…")
  // 3. Auto-start the timer + ambient music
  useEffect(() => {
    setSecondsLeft(mission.duration_minutes * 60);
    setSecondsElapsed(0);
    setRunning(true);
    setLastStars(null);
    endedRef.current = false;
    startedAtRef.current = Date.now();

    if (musicOn) startAmbient(mission.category, mission.music_url);

    if (audioOn) {
      // Tiny delay so the page paints before TTS kicks in.
      const t = setTimeout(() => speak(mission.child_message, mission.character, mission.voice_url, mission.voice_pitch_boost), 250);
      return () => {
        clearTimeout(t);
        stopSpeaking();
      };
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission.id]);

  // Toggle music live (don't restart the mission).
  useEffect(() => {
    if (musicOn) startAmbient(mission.category, mission.music_url);
    else stopAmbient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicOn]);

  // The countdown itself (also drives the elapsed-time counter so the
  // minimum-duration lock can release in real time).
  useEffect(() => {
    if (!running) return undefined;
    const tick = setInterval(() => {
      setSecondsElapsed((e) => e + 1);
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tick);
          setRunning(false);
          if (!endedRef.current) {
            endedRef.current = true;
            playEndChime();
            toast.success("✨ Mission terminée ! Bravo.");
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [running]);

  const finishMission = async (rating) => {
    setSubmittingRating(true);
    const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
    try {
      stopSpeaking();
      stopAmbient();
      await api.submitFeedback({
        journey_id: journeyId,
        mission_id: mission.id,
        rating,
      });
      const payload = await api.completeMission(journeyId, mission.id, elapsed);

      const earned = payload.awarded_stars ?? 0;
      setLastStars(earned);
      playStarDing(earned);
      // The full congrats cocktail: chime + cheer "yay!" + applause crowd.
      // Triggered immediately so the kid hears it even if they tap fast.
      playCongratsBurst();
      toast.success(
        `${"⭐".repeat(earned)}  Mission terminée en ${formatTime(elapsed)}`,
        { duration: 3000 },
      );

      // Let the stars animation breathe ~800ms before handing off to the
      // transition screen (which then plays the spoken congrats + the
      // next-mission announcement).
      setTimeout(() => onComplete(payload), 800);
    } catch (err) {
      console.error(err);
      toast.error("Impossible d'enregistrer la mission.");
    } finally {
      setSubmittingRating(false);
    }
  };

  const replayVoice = () => speak(mission.child_message, mission.character, mission.voice_url, mission.voice_pitch_boost);

  const cat = CATEGORY_LABEL[mission.category] ?? { label: mission.category, emoji: "✨" };
  const isLyria = mission.character === "lyria";

  return (
    <section className="rounded-3xl bg-white/85 p-6 shadow-md backdrop-blur">
      <div className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-[#7a6b8a]">
        <div className="flex items-center gap-2">
          <span>{cat.emoji}</span>
          <span>{cat.label}</span>
          <span>·</span>
          <span>{mission.duration_minutes} min</span>
          {mission.start_time && (
            <>
              <span>·</span>
              <span>🕓 {mission.start_time}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setMusicOn((v) => !v)}
            aria-label={musicOn ? "Couper la musique" : "Activer la musique"}
            className="rounded-full bg-[#fdf3e8] px-2 py-1 text-xs text-[#5b4b6b] hover:bg-[#fbe4cd]"
          >
            {musicOn ? "🎵 musique" : "🔇 musique"}
          </button>
          <button
            type="button"
            onClick={() => {
              setAudioOn((v) => {
                if (v) stopSpeaking();
                return !v;
              });
            }}
            aria-label={audioOn ? "Couper la voix" : "Activer la voix"}
            className="rounded-full bg-[#fdf3e8] px-2 py-1 text-xs text-[#5b4b6b] hover:bg-[#fbe4cd]"
          >
            {audioOn ? "🔊 voix" : "🔇 voix"}
          </button>
        </div>
      </div>

      {lastStars !== null && (
        <div className="mb-3 animate-pulse rounded-2xl bg-gradient-to-br from-[#fff4cc] to-[#ffe6d6] px-4 py-3 text-center shadow-sm">
          <div className="text-3xl">{"⭐".repeat(lastStars)}{"☆".repeat(Math.max(0, 3 - lastStars))}</div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[#7a6b8a]">
            {lastStars === 3 ? "Parfait !" : lastStars === 2 ? "Bravo !" : "Très bien !"}
          </div>
        </div>
      )}

      <div className="mt-1 flex items-center gap-4">
        <Mascot character={mission.character} size={72} talking={audioOn} />
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#7a6b8a]">
            {isLyria ? "🦋 Lyria" : "🦊 Neo"}
          </p>
          <h2 className="font-[Fredoka] text-2xl font-semibold text-[#3b2a4a]">
            {mission.title}
          </h2>
        </div>
      </div>

      <button
        type="button"
        onClick={replayVoice}
        className={[
          "mt-4 w-full rounded-2xl p-4 text-left text-sm transition hover:opacity-90",
          isLyria ? "bg-[#f3e8ff] text-[#5b3d8a]" : "bg-[#ffe6d6] text-[#8a4b2c]",
        ].join(" ")}
        title="Cliquer pour réécouter"
      >
        <p className="flex items-center justify-between font-medium">
          <span>{isLyria ? "🦋 Lyria" : "🦊 Neo"} :</span>
          <span aria-hidden className="text-base">🔁</span>
        </p>
        <p className="mt-1">{mission.child_message}</p>
      </button>

      <p className="mt-4 text-sm leading-relaxed text-[#3b2a4a]">
        {mission.instructions}
      </p>

      <div className="mt-6 flex flex-col items-center gap-4">
        <div className="font-[Fredoka] text-5xl font-semibold text-[#3b2a4a] tabular-nums">
          {formatTime(secondsLeft)}
        </div>
        <Button
          onClick={() => setRunning((r) => !r)}
          className="rounded-full bg-[#ff8a65] px-8 py-5 text-base font-semibold text-white shadow-lg hover:bg-[#ff7043]"
        >
          {running
            ? "Pause"
            : secondsLeft === 0
              ? "Recommencer le minuteur"
              : "Reprendre"}
        </Button>
        {secondsLeft === 0 && (
          <p className="text-xs text-[#7a6b8a]">Le temps est écoulé — passez à la suite.</p>
        )}
      </div>

      <PhotoUploader journeyId={journeyId} missionId={mission.id} />

      <div className="mt-6 border-t border-[#f0e6f5] pt-4">
        <p className="mb-3 text-center text-sm font-medium text-[#3b2a4a]">
          {canFinish
            ? "Comment s'est passée la mission ?"
            : `🔒 Encore ${formatTime(minLockRemaining)} avant de pouvoir terminer`}
        </p>
        <div className="grid grid-cols-3 gap-2">
          {RATINGS.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={submittingRating || !canFinish}
              onClick={() => finishMission(r.id)}
              className="flex flex-col items-center gap-1 rounded-2xl bg-[#fdf3e8] py-3 text-xs font-medium text-[#3b2a4a] shadow-sm transition hover:bg-[#fbe4cd] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-2xl" aria-hidden>{r.emoji}</span>
              {r.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Soft pause between two missions. Plays the spoken "Bravo!" line, then
 * the announcement for the next mission, with a visible countdown so the
 * kid can breathe / drink water before the next activity starts.
 *
 * The parent can tap "On y va !" to skip the countdown at any moment.
 */
function TransitionCard({ data, onContinue }) {
  // Pause duration: each *just-finished* mission carries its own
  // pause_after_minutes (set by the admin in the planning). Default to
  // 20 seconds when no schedule is in play.
  const pauseMinutes = data.finishedMission?.pause_after_minutes;
  const TRANSITION_SECONDS = pauseMinutes !== undefined && pauseMinutes !== null
    ? Math.max(5, Math.round(Number(pauseMinutes) * 60))
    : 20;
  const [seconds, setSeconds] = useState(TRANSITION_SECONDS);
  const playedRef = useRef(false);

  useEffect(() => {
    if (playedRef.current) return;
    playedRef.current = true;

    // 1. spoken "Bravo !" right away
    speak(data.congratsText, data.congratsChar || "neo", data.congratsUrl, data.congratsPitch);
    // 2. transition announce ~2.6s later
    const transitionTimer = setTimeout(() => {
      speak(data.transitionText, data.transitionChar || "neo", data.transitionUrl, data.transitionPitch);
    }, 2600);

    return () => {
      clearTimeout(transitionTimer);
      stopSpeaking();
    };
  }, [data]);

  // Countdown — auto-advance at zero.
  useEffect(() => {
    if (seconds <= 0) {
      onContinue();
      return undefined;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, onContinue]);

  const next = data.nextMission;

  return (
    <section className="rounded-3xl bg-gradient-to-br from-[#e8f5e9] to-[#f1faf3] p-6 text-center shadow-md">
      <div className="text-5xl">👏</div>
      <h2 className="mt-2 font-[Fredoka] text-2xl font-semibold text-[#2e6a3a]">
        Bravo, mission terminée !
      </h2>
      <p className="mt-1 text-sm text-[#4b6a52]">
        Prends quelques secondes : respire, bois un peu d'eau, étire-toi.
      </p>

      <div className="mt-6 rounded-3xl bg-white/80 p-5">
        <div className="flex items-center justify-center gap-3">
          <Mascot character={next.character} size={56} talking />
          <div className="text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#7a6b8a]">
              Prochaine mission
            </p>
            <p className="font-[Fredoka] text-lg font-semibold text-[#3b2a4a]">
              {next.title}
            </p>
            <p className="text-xs text-[#5b4b6b]">
              {next.start_time ? `🕓 ${next.start_time} · ` : ""}
              {next.duration_minutes} min · {next.character === "lyria" ? "🦋 Lyria" : "🦊 Neo"}
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2 text-sm font-medium text-[#3b2a4a]">
          <span>Démarre dans</span>
          <span className="rounded-full bg-[#ff8a65] px-3 py-1 text-white tabular-nums">
            {seconds}s
          </span>
        </div>

        <Button
          type="button"
          onClick={onContinue}
          className="mt-5 w-full rounded-full bg-[#ff8a65] py-5 text-base font-semibold text-white shadow-lg hover:bg-[#ff7043]"
        >
          On y va ✨
        </Button>
      </div>
    </section>
  );
}

function PhotoUploader({ journeyId, missionId }) {
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const onPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await api.uploadMissionPhoto(journeyId, missionId, file);
      setDone(true);
      toast.success("📸 Photo ajoutée au journal");
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.status === 401) {
        toast.message("Connectez-vous pour ajouter une photo au journal privé.");
      } else {
        toast.error("Photo non enregistrée.");
      }
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl bg-[#fdf3e8]/60 p-3 text-center">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-xs font-medium text-[#3b2a4a] shadow-sm hover:bg-white">
        <span aria-hidden>📸</span>
        <span>{done ? "Photo ajoutée — en ajouter une autre" : uploading ? "Envoi…" : "Capturer un souvenir (privé)"}</span>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPick}
          disabled={uploading}
        />
      </label>
    </div>
  );
}

function UpcomingList({ missions }) {
  const upcoming = missions.filter((m) => m.status === "pending");
  if (upcoming.length === 0) return null;
  return (
    <section className="mt-8">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#7a6b8a]">
        À venir
      </h3>
      <ul className="space-y-2">
        {upcoming.map((m) => {
          const cat = CATEGORY_LABEL[m.category] ?? { label: m.category, emoji: "✨" };
          return (
            <li
              key={m.id}
              className="flex items-center justify-between rounded-2xl bg-white/60 px-4 py-3 text-sm text-[#3b2a4a]"
            >
              <span className="flex items-center gap-2">
                <span aria-hidden>{cat.emoji}</span>
                {m.title}
              </span>
              <span className="text-xs text-[#7a6b8a]">{m.duration_minutes} min</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function JourneyComplete({ journey }) {
  return (
    <section className="rounded-3xl bg-white/85 p-8 text-center shadow-md">
      <div className="text-5xl">🌟</div>
      <h2 className="mt-3 font-[Fredoka] text-2xl font-semibold text-[#3b2a4a]">
        Matinée magique terminée !
      </h2>
      <p className="mt-2 text-sm text-[#5b4b6b]">
        Neo et Lyria sont très fiers. Petit bilan dans un instant…
      </p>
    </section>
  );
}
